import React, { useContext, useState } from 'react';
import SeeAllInclusionsLink from '../Components/Common/SeeAllInclusionsLink';
import {
  useResumeSubscription,
  useSubscription,
} from '../Hooks/useSubscription';
import { HorizontalDivider } from '../Components/Common/HorizontalDivider';
import { Title } from '../Components/Common/Title';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Subtitle } from '../Components/Common/Subtitle';
import {
  AnimatedDiv,
  ShowAnimation,
} from '@vimily/bonjoro-ui-components-v2/AnimatedDiv';
import { useHistory, useParams } from 'react-router';
import { Button, ButtonVariant } from '@vimily/bonjoro-ui-components-v2/Button';
import { LoadingProgress } from '@vimily/bonjoro-ui-components-v2/LoadingProgress';
import { useTrialProgress } from '../Hooks/useTrialProgress';
import { Link } from 'react-router-dom';
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import { useCustomerCoupon } from '~/App/Api/Coupon';
import { PlanProductCard } from '../Components/Subscription/PlanProductCard.tsx';
import { BillingCycle, Service } from '~/types/models';
import { ChangePlanPanel } from '../Components/Subscription/ChangePlanPanel';
import { CustomerSupportPanel } from '../Components/Subscription/CustomerSupportPanel';
import { useAddOnsPrices } from '../Hooks/useAddOnsPrice';
import { AvailableAddOns } from '../Components/Subscription/AvailableAddOns';
import FlashContext from '~/App/Components/Flash/Context';
import { RedeeemCouponCta } from '../Components/Common/RedeemCouponCta';
import { billingRoutes } from '..';
import { mapAddOnToAddOnItem } from '../utils';
import LegacyChangePlanModal from '../../Billing/Components/LegacyChangePlanModal';
import useCurrentTeam from '~/react-hooks/use-current-team';
import { confirmAsync } from '~/App/Components/Modals/Confirm';

export function Subscription() {
  const { planSlug } = useParams<{ planSlug: string }>();
  const { data: subscription } = useSubscription({ slug: planSlug });

  const { data: coupon, isLoading } = useCustomerCoupon({});
  const { progress, maxProgress } = useTrialProgress(subscription);
  const { data: addOnPrices } = useAddOnsPrices({});
  const { on_legacy_plan: onLegacyPlan } = useCurrentTeam();

  const history = useHistory();
  const { createFlash } = useContext(FlashContext);
  const { mutateAsync: resumeSubscription, isLoading: isResumingSubscription } =
    useResumeSubscription({
      options: {
        onSuccess: () => {
          createFlash({
            id: 'resume-subscription-success',
            title: 'Success',
            message: 'Subscription resumed successfully',
          });

          window.location.reload();
        },
        onError: ({ message }) => {
          createFlash({
            id: 'resume-subscription-error',
            title: 'Something went wrong',
            message: message ?? 'Error',
            options: { style: 'danger' },
          });
        },
      },
    });
  const [showChangeLegacyPlanModal, setShowChangeLegacyPlanModal] =
    useState(false);
  const onChangePlanClicked = () => {
    if (onLegacyPlan) {
      setShowChangeLegacyPlanModal(true);
    } else {
      history.push(`${billingRoutes.pricing}/${subscription?.service ?? ''}`);
    }
  };

  const handleResumeSubscription = async () => {
    if (
      await confirmAsync(
        'Are you sure you want to resume this subscription?',
        'Yes, resume subscription',
        'No, cancel'
      )
    ) {
      await resumeSubscription(planSlug);
    }
  };

  const pricingCta = subscription?.cancelled ? (
    <Button
      variant={ButtonVariant.Primary}
      className='py-2 w-full md:w-auto'
      onClick={handleResumeSubscription}
      disabled={isResumingSubscription}
    >
      {isResumingSubscription && (
        <span className='fa fa-spinner spinning mr-2' />
      )}
      Resume subscription
    </Button>
  ) : (
    <Button
      variant={ButtonVariant.Primary}
      className='py-2 w-full md:w-auto'
      onClick={onChangePlanClicked}
    >
      {subscription?.is_free ? 'Upgrade' : 'Change plan'}
    </Button>
  );

  const mainCtaButtons = (
    <>
      <SeeAllInclusionsLink
        trackingContext='manage subscription'
        className='w-full md:w-auto'
        service={subscription?.service}
      />
      {pricingCta}
    </>
  );

  if (!subscription || !addOnPrices) return <></>;

  const addOns = mapAddOnToAddOnItem(
    subscription?.addons,
    subscription.service
  );

  const billingDescription =
    subscription?.billing_cycle === BillingCycle.Monthly
      ? 'Billed monthly (USD)'
      : `Billed ${subscription?.basic_price} USD annually`;

  const showCouponCodeCta =
    (!coupon || (coupon && Object.keys(coupon).length === 0)) &&
    !isLoading &&
    !subscription.is_free;

  return (
    <AnimatedDiv
      show
      showAnimation={ShowAnimation.FadeIn150}
      className='flex flex-col gap-y-5 max-w-[1200px]'
    >
      <div className='flex flex-col xmd:flex-row gap-y-6 justify-between mb-2 w-full '>
        <div className='flex flex-row items-center justify-start gap-x-4 flex-wrap gap-y-4 w-full'>
          <div className='flex flex-row gap-x-4'>
            <FontAwesomeIcon
              icon={faChevronLeft}
              className='text-[35px] cursor-pointer v2-text-neutral-900 hover:v2-text-yellow-500'
              onClick={() => history.push('/billing')}
            />
            <Title label='Manage subscription' />
          </div>
          <RedeeemCouponCta show={showCouponCodeCta} />
        </div>

        <div
          className={cx([
            'flex flex-col items-center justify-center w-full',
            'min-[0px]:hidden lg:flex invisible lg:visible gap-x-4 gap-y-4 flex-wrap',
            'flex-col-reverse md:flex-row md:justify-end',
          ])}
        >
          {mainCtaButtons}
        </div>
      </div>

      <Subtitle label='Your current plan' />
      {progress != undefined && (
        <LoadingProgress
          progress={progress}
          maxProgress={maxProgress}
          className='border border-solid border-neutral-300 !p-[3px]'
          progressClassName={cx({
            'v2-bg-blue-500': subscription?.service === Service.VideoMessaging,
            'v2-bg-magenta-500': subscription?.service === Service.Testimonials,
          })}
          labelClassName='font-bold text-base v2-text-neutral-900'
          label={
            subscription?.cancelled
              ? subscription.cancelled_days_left_text
              : subscription?.trial_info.days_left_text
          }
        />
      )}
      <PlanProductCard
        title={subscription?.name}
        planDescription={subscription?.description}
        imgUrl={subscription.image}
        discountFormat={
          !subscription?.is_free ? subscription.discount_formatted : undefined
        }
        billingPrice={
          !subscription?.is_free
            ? subscription?.monthly_price_formatted
            : undefined
        }
        billingDescription={
          !subscription?.is_free ? billingDescription : undefined
        }
        featuresList={subscription?.feature_descriptions?.active}
        usages={subscription?.usages}
      />
      {!subscription.is_free && (
        <>
          <div
            className={cx([
              'flex flex-col items-center justify-center w-full',
              'visible lg:invisible lg:hidden gap-x-4 gap-y-4 flex-wrap',
              'flex-col-reverse md:flex-row md:justify-end',
            ])}
          >
            {mainCtaButtons}
          </div>

          <AvailableAddOns
            addOnPrices={addOnPrices}
            addOns={addOns}
            service={subscription.service}
            billingCycle={subscription.billing_cycle}
          />

          {!subscription?.cancelled && (
            <>
              <HorizontalDivider />
              <Title label='Thinking of cancelling subscription?' />
              <div className='flex flex-col lg:flex-row gap-x-6 gap-y-4 '>
                <CustomerSupportPanel />
                <ChangePlanPanel service={subscription.service} />
              </div>
              <Link
                to={`/billing/subscriptions/cancel/${planSlug}`}
                className='pb-[30px]'
              >
                <Button
                  variant={ButtonVariant.DangerWhite}
                  className='py-2 v2-text-neutral-900'
                >
                  Cancel subscription
                </Button>
              </Link>
            </>
          )}
        </>
      )}
      <LegacyChangePlanModal
        open={showChangeLegacyPlanModal}
        onModalClose={() => setShowChangeLegacyPlanModal(false)}
      />
    </AnimatedDiv>
  );
}
